import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PronunciationTestBench } from "./PronunciationTestBench";
import {
  ArrowLeft, Loader2, Volume2, Sparkles, CheckCircle2, XCircle, Undo2, Pencil, Plus,
  Trash2, Play, AlertTriangle, RadioTower, Coins, BookText, Star, Search, Shuffle, MapPin,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATS = {
  person: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  place: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  brand: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  organization: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  title: "bg-fuchsia-500/15 text-fuchsia-300 ring-fuchsia-500/30",
  acronym: "bg-indigo-500/15 text-indigo-300 ring-indigo-500/30",
  abbreviation: "bg-indigo-500/15 text-indigo-300 ring-indigo-500/30",
  foreign_word: "bg-rose-500/15 text-rose-300 ring-rose-500/30",
  loanword: "bg-rose-500/15 text-rose-300 ring-rose-500/30",
  invented_term: "bg-violet-500/15 text-violet-300 ring-violet-500/30",
  culinary: "bg-orange-500/15 text-orange-300 ring-orange-500/30",
  wine: "bg-orange-500/15 text-orange-300 ring-orange-500/30",
  homograph: "bg-teal-500/15 text-teal-300 ring-teal-500/30",
  ambiguous: "bg-slate-600/25 text-slate-300 ring-slate-500/30",
};
const STATUS = {
  draft: { label: "Draft", c: "bg-amber-500/15 text-amber-300 ring-amber-500/30" },
  approved: { label: "Approved", c: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30" },
  rejected: { label: "Rejected", c: "bg-red-500/15 text-red-300 ring-red-500/30" },
};
const CATEGORY_OPTIONS = ["person", "place", "brand", "organization", "title", "acronym", "abbreviation", "foreign_word", "loanword", "invented_term", "culinary", "wine", "homograph", "ambiguous"];
const SCOPE_OPTIONS = [
  { v: "occurrence", label: "This occurrence" },
  { v: "passage", label: "Passage" },
  { v: "chapter", label: "Chapter" },
  { v: "book", label: "This book" },
  { v: "series", label: "Series" },
  { v: "character", label: "Character" },
  { v: "global", label: "Global library" },
];

function Chip({ className = "", children, ...rest }) {
  return <span className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-wider ring-1 ${className}`} {...rest}>{children}</span>;
}

/* ---------------- Audition (preflight + confirm + comparison) ---------------- */
function AuditionDialog({ pron, onClose, onGenerated }) {
  const [mode, setMode] = useState("sentence");
  const [applyDict, setApplyDict] = useState(false);
  const [spokenOverride, setSpokenOverride] = useState("");
  const [occIndex, setOccIndex] = useState(0);
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [auditions, setAuditions] = useState([]);

  const loadAuditions = useCallback(async () => {
    try {
      const { data } = await api.get(`/pronunciations/${pron.id}/auditions`);
      setAuditions(data);
    } catch { /* ignore */ }
  }, [pron.id]);

  const runPreflight = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/pronunciations/${pron.id}/audition/preflight`, {
        mode, apply_dictionary: applyDict, spoken_form: spokenOverride || undefined, occurrence_index: occIndex,
      });
      setPreflight(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [pron.id, mode, applyDict, spokenOverride, occIndex]);

  useEffect(() => { runPreflight(); loadAuditions(); }, [runPreflight, loadAuditions]);

  const confirmGenerate = async () => {
    if (!window.confirm("This makes a PAID ElevenLabs call with Scott and consumes credits. Continue?")) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/pronunciations/${pron.id}/audition`, {
        mode, apply_dictionary: applyDict, spoken_form: spokenOverride || undefined, occurrence_index: occIndex, confirm: true,
      });
      toast.success(data.cache_hit ? "Played a cached audition — no credits used." : "Audition generated.");
      await loadAuditions();
      await runPreflight();
      if (onGenerated) onGenerated();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const alts = pron.alternates || [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl border-border bg-[#0d1016] text-slate-200" data-testid="audition-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-slate-100">Audition · {pron.written_form}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Scott voices a manuscript sentence with the intended pronunciation. Review the credit estimate before any paid call.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-400">Mode</Label>
              <div className="flex overflow-hidden rounded-md ring-1 ring-border">
                <button data-testid="audition-mode-sentence" onClick={() => setMode("sentence")}
                  className={`px-3 py-1.5 text-xs ${mode === "sentence" ? "bg-amber-500/15 text-amber-200" : "text-slate-400"}`}>Manuscript sentence</button>
                <button data-testid="audition-mode-isolated" onClick={() => setMode("isolated")}
                  className={`px-3 py-1.5 text-xs ${mode === "isolated" ? "bg-amber-500/15 text-amber-200" : "text-slate-400"}`}>Isolated word</button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="audition-apply-dictionary" checked={applyDict} onCheckedChange={setApplyDict} />
              <Label className="text-xs text-slate-400">Apply synced dictionary to original spelling</Label>
            </div>
          </div>

          {alts.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-xs text-slate-400">Spoken form</Label>
              <button onClick={() => setSpokenOverride("")}
                className={`rounded-md px-2 py-1 text-xs ring-1 ${!spokenOverride ? "bg-amber-500/15 text-amber-200 ring-amber-500/30" : "text-slate-400 ring-border"}`}>
                {pron.spoken_form || pron.written_form} (primary)
              </button>
              {alts.map((a, i) => (
                <button key={i} onClick={() => setSpokenOverride(a.spoken_form)}
                  className={`rounded-md px-2 py-1 text-xs ring-1 ${spokenOverride === a.spoken_form ? "bg-amber-500/15 text-amber-200 ring-amber-500/30" : "text-slate-400 ring-border"}`}>
                  {a.spoken_form}{a.label ? ` · ${a.label}` : ""}
                </button>
              ))}
            </div>
          )}

          {mode === "sentence" && preflight && preflight.occurrence_count > 1 && (
            <div className="rounded-lg border border-border bg-[#12161f] p-3" data-testid="occurrence-selector">
              <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                <MapPin className="h-3 w-3 text-teal-400" /> Target occurrence ({preflight.occurrence_count} in manuscript)
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(preflight.occurrences || []).map((o, i) => (
                  <button key={i} data-testid={`occurrence-opt-${i}`} onClick={() => setOccIndex(i)}
                    title={o.snippet}
                    className={`max-w-[220px] truncate rounded-md px-2 py-1 text-[11px] ring-1 ${occIndex === i ? "bg-teal-500/15 text-teal-200 ring-teal-500/40" : "text-slate-400 ring-border hover:text-slate-200"}`}>
                    ¶{o.body_number} · {o.snippet}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg border border-border bg-[#12161f] p-4">
            {loading || !preflight ? (
              <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Estimating…</div>
            ) : (
              <div className="space-y-3" data-testid="preflight-panel">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded bg-slate-800/60 px-2 py-0.5 text-slate-300">Display <span className="font-mono text-amber-300">{preflight.display_form || pron.written_form}</span></span>
                  <span className="rounded bg-slate-800/60 px-2 py-0.5 text-slate-300">Generation <span className="font-mono text-teal-300" data-testid="preflight-generation-form">{preflight.generation_form || preflight.display_form || pron.written_form}</span></span>
                  {preflight.phonetic && <span className="rounded bg-slate-800/60 px-2 py-0.5 text-slate-300">Phonetic <span className="font-mono text-slate-200">/{preflight.phonetic}/</span> <span className="uppercase text-slate-500">{preflight.phonetic_alphabet}</span></span>}
                </div>
                {mode === "sentence" ? (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="rounded-md border border-border bg-[#0d1016] p-3">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Original manuscript sentence (unchanged)</div>
                      <p className="mt-1 text-sm italic text-slate-400" data-testid="preflight-original">“{preflight.original_sentence}”</p>
                    </div>
                    <div className="rounded-md border border-teal-500/25 bg-teal-500/5 p-3">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-teal-400/80">Generation text → ElevenLabs</div>
                      <p className="mt-1 text-sm italic text-slate-100" data-testid="preflight-text">“{preflight.text}”</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Text Scott will speak</div>
                    <p className="mt-1 text-sm italic text-slate-200" data-testid="preflight-text">“{preflight.text}”</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Metric label="Characters" value={preflight.characters} testid="preflight-characters" />
                  <Metric label="Requests" value={preflight.requests} />
                  <Metric label="Est. credits" value={preflight.estimated_credits} testid="preflight-credits" tone="amber" />
                  <Metric label="Output" value={preflight.output_format} small />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1.5 rounded border border-sky-500/30 bg-sky-500/[0.06] px-2 py-0.5" data-testid="audition-profile-badge"><span className="font-mono uppercase tracking-wider text-sky-300">Audition profile</span><span className="text-slate-300">{preflight.output_format_label || preflight.output_format}</span></span>
                  <span><Coins className="mr-1 inline h-3 w-3 text-amber-400" />{preflight.billing_rate_label}</span>
                  <span>Model: <span className="font-mono text-slate-300">{preflight.model}</span></span>
                  <span>Voice: <span className="text-slate-300">{preflight.voice_name}</span></span>
                </div>
                {preflight.cache_hit && (
                  <div className="rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300" data-testid="preflight-cache-hit">
                    <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> An identical audition is cached — replaying it will not consume credits.
                  </div>
                )}
                {applyDict && !preflight.dictionary_synced && (
                  <div className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                    <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> No dictionary is synced yet. Sync approved rules to ElevenLabs first.
                  </div>
                )}
                {!preflight.provider_configured && (
                  <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-300">
                    <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> No ElevenLabs key configured. Add it in Settings before auditioning.
                  </div>
                )}
              </div>
            )}
          </div>

          {auditions.length > 0 && (
            <div>
              <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Cached auditions · side-by-side</div>
              <div className="space-y-2">
                {auditions.map((a) => (
                  <div key={a.id} className="rounded-lg border border-border bg-[#12161f] p-3" data-testid={`audition-cached-${a.id}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-slate-300">{a.label} <span className="text-slate-500">· {a.mode}{a.apply_dictionary ? " · dictionary" : ""}</span></span>
                      <span className="font-mono text-slate-500">{a.char_count} chars · {a.actual_credits ?? a.estimated_credits} cr</span>
                    </div>
                    <audio controls preload="none" className="h-8 w-full" src={`${BACKEND}${a.audio_url}`} data-testid={`audition-audio-${a.id}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-slate-400">Close</Button>
          <Button
            data-testid="audition-confirm-generate"
            onClick={confirmGenerate}
            disabled={generating || loading}
            className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {preflight?.cache_hit ? "Play cached (0 credits)" : "Confirm & generate (paid)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value, tone, small, testid }) {
  const c = tone === "amber" ? "text-amber-300" : "text-slate-200";
  return (
    <div className="rounded-md border border-border bg-[#0d1016] px-3 py-2" data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-mono ${small ? "text-xs" : "text-lg"} ${c}`}>{value}</div>
    </div>
  );
}

/* ---------------- Edit / Add dialogs ---------------- */
function PronForm({ initial, onSave, onClose, title, allowDelete, onDelete }) {
  const [f, setF] = useState({
    written_form: initial?.written_form || "", spoken_form: initial?.spoken_form || "",
    generation_form: initial?.generation_form || "",
    phonetic: initial?.phonetic || "", phonetic_alphabet: initial?.phonetic_alphabet || "none",
    category: initial?.category || "person", scope: initial?.scope || "book", notes: initial?.notes || "",
    alternates: initial?.alternates || [],
    entry_type: initial?.entry_type || "term", sense: initial?.sense || "",
  });
  const [alt, setAlt] = useState({ spoken_form: "", label: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isHomo = f.entry_type === "homograph";

  const save = () => {
    const payload = {
      written_form: f.written_form, spoken_form: f.spoken_form, generation_form: f.generation_form,
      phonetic: f.phonetic,
      phonetic_alphabet: f.phonetic_alphabet === "none" ? null : f.phonetic_alphabet,
      category: isHomo ? "homograph" : f.category, scope: f.scope, notes: f.notes, alternates: f.alternates,
      entry_type: f.entry_type, sense: f.sense,
    };
    onSave(payload);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg border-border bg-[#0d1016] text-slate-200" data-testid="pron-form-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-slate-100">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">Everything you add or edit stays in Draft until you approve it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Written form</Label>
            <Input data-testid="pron-written-form" value={f.written_form} onChange={(e) => set("written_form", e.target.value)} className="mt-1 bg-[#12161f]" />
          </div>
          <div className="flex items-center gap-2">
            <Switch data-testid="pron-homograph-toggle" checked={isHomo} onCheckedChange={(v) => set("entry_type", v ? "homograph" : "term")} />
            <Label className="text-xs text-slate-400">Context-sensitive homograph (applied to generation text only, no global rule)</Label>
          </div>
          {isHomo && (
            <div>
              <Label className="text-xs text-slate-400">Intended sense / meaning</Label>
              <Input data-testid="pron-sense" value={f.sense} onChange={(e) => set("sense", e.target.value)} placeholder="e.g. musical bass (instrument)" className="mt-1 bg-[#12161f]" />
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-400">Display pronunciation (respelling)</Label>
            <Input data-testid="pron-spoken-form" value={f.spoken_form} onChange={(e) => set("spoken_form", e.target.value)} placeholder="e.g. mah-ree-SOHL" className="mt-1 bg-[#12161f]" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Generation replacement <span className="text-slate-600">(sent to ElevenLabs; leave blank to use display)</span></Label>
            <Input data-testid="pron-generation-form" value={f.generation_form} onChange={(e) => set("generation_form", e.target.value)} placeholder="e.g. base" className="mt-1 bg-[#12161f]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Phonetic (IPA/CMU)</Label>
              <Input data-testid="pron-phonetic" value={f.phonetic} onChange={(e) => set("phonetic", e.target.value)} placeholder="optional" className="mt-1 bg-[#12161f]" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Alphabet</Label>
              <Select value={f.phonetic_alphabet} onValueChange={(v) => set("phonetic_alphabet", v)}>
                <SelectTrigger className="mt-1 bg-[#12161f]" data-testid="pron-alphabet"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="ipa">IPA</SelectItem>
                  <SelectItem value="cmu">CMU</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Category</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1 bg-[#12161f]" data-testid="pron-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Scope</Label>
              <Select value={f.scope} onValueChange={(v) => set("scope", v)}>
                <SelectTrigger className="mt-1 bg-[#12161f]" data-testid="pron-scope"><SelectValue /></SelectTrigger>
                <SelectContent>{SCOPE_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Textarea data-testid="pron-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 bg-[#12161f]" rows={2} />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Alternate pronunciations (for comparison)</Label>
            <div className="mt-1 space-y-1">
              {f.alternates.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 rounded bg-[#12161f] px-2 py-1 text-slate-300">{a.spoken_form}{a.label ? ` · ${a.label}` : ""}</span>
                  <button onClick={() => set("alternates", f.alternates.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Input value={alt.spoken_form} onChange={(e) => setAlt({ ...alt, spoken_form: e.target.value })} placeholder="alternate spoken form" className="bg-[#12161f]" data-testid="pron-alt-spoken" />
                <Input value={alt.label} onChange={(e) => setAlt({ ...alt, label: e.target.value })} placeholder="label" className="w-32 bg-[#12161f]" />
                <Button size="sm" variant="outline" data-testid="pron-alt-add"
                  onClick={() => { if (alt.spoken_form.trim()) { set("alternates", [...f.alternates, alt]); setAlt({ spoken_form: "", label: "" }); } }}>Add</Button>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {allowDelete && <Button variant="ghost" onClick={onDelete} className="mr-auto text-red-400 hover:bg-red-500/10">Delete</Button>}
          <Button variant="ghost" onClick={onClose} className="text-slate-400">Cancel</Button>
          <Button data-testid="pron-form-save" onClick={save} className="bg-amber-500 text-slate-950 hover:bg-amber-400">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Card ---------------- */
function PronCard({ p, projectId, onAudition, onEdit, onChange }) {
  const navigate = useNavigate();
  const act = async (verb) => {
    try {
      await api.post(`/pronunciations/${p.id}/${verb}`);
      onChange();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border border-border bg-[#12161f] p-4" data-testid={`pron-card-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-serif text-lg text-slate-100">{p.written_form}</h3>
            {p.entry_type === "homograph" && <Chip className="bg-teal-500/20 text-teal-200 ring-teal-500/40"><Shuffle className="mr-0.5 inline h-2.5 w-2.5" />Homograph</Chip>}
            {p.first_audition_set && <Chip className="bg-amber-500/20 text-amber-200 ring-amber-500/40"><Star className="mr-0.5 inline h-2.5 w-2.5" />First set</Chip>}
            {p.entry_type !== "homograph" && <Chip className={CATS[p.category] || CATS.ambiguous}>{(p.category || "").replace(/_/g, " ")}</Chip>}
            <Chip className={STATUS[p.status]?.c}>{STATUS[p.status]?.label}</Chip>
            {p.verdict && <Chip className="bg-slate-700/40 text-slate-200 ring-slate-500/30">{p.verdict.replace(/_/g, " ")}</Chip>}
            {p.ambiguous && p.entry_type !== "homograph" && <Chip className="bg-slate-600/25 text-slate-300 ring-slate-500/30">Ambiguous</Chip>}
          </div>
          {p.entry_type === "homograph" && p.sense && (
            <p className="mt-1 text-xs text-teal-300/90"><span className="uppercase tracking-wider text-teal-500/70">Sense</span> · {p.sense}</p>
          )}
          <p className="mt-1 font-mono text-sm text-amber-300">{p.spoken_form || <span className="text-slate-500">no spoken form yet</span>}</p>
          {p.phonetic && <p className="mt-0.5 font-mono text-xs text-slate-400">/{p.phonetic}/ <span className="uppercase text-slate-600">{p.phonetic_alphabet}</span></p>}
          {p.status_note && <p className="mt-1 text-xs text-slate-500">{p.status_note}</p>}
          {p.scan_reason && <p className="mt-1 text-xs text-indigo-300/80"><Search className="mr-1 inline h-3 w-3" />{p.scan_reason}</p>}
          {p.audition_note && <p className="mt-1 text-xs italic text-slate-500">{p.audition_note}</p>}
          {p.entry_type === "homograph" && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px]">
              {p.generation_form && <span className="rounded bg-teal-500/10 px-1.5 py-0.5 text-teal-300">reads as <span className="font-mono">“{p.generation_form}”</span> in generation</span>}
              <span className="rounded bg-teal-500/10 px-1.5 py-0.5 text-teal-300"><MapPin className="mr-0.5 inline h-2.5 w-2.5" />{p.occurrences?.length || 0} occurrence{(p.occurrences?.length || 0) === 1 ? "" : "s"} · scope {p.scope}</span>
              <span className="rounded bg-slate-800/60 px-1.5 py-0.5 text-slate-400">Manuscript unchanged · generation-text only · no global rule</span>
            </div>
          )}
          {p.evidence?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {p.evidence.slice(0, 6).map((e, i) => (
                <button key={i} data-testid={`pron-evidence-${p.id}-${i}`}
                  onClick={() => e.order != null && navigate(`/studio/projects/${projectId}/manuscript?focus=${e.order}`)}
                  className="rounded bg-slate-800/60 px-1.5 py-0.5 font-mono text-[10px] text-slate-400 hover:bg-amber-500/10 hover:text-amber-300">
                  ¶{e.body_number}
                </button>
              ))}
            </div>
          )}
          <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-600">
            {p.source?.replace(/_/g, " ")} · scope {p.scope}
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-1.5">
          <Button size="sm" variant="outline" data-testid={`pron-audition-${p.id}`} onClick={() => onAudition(p)} className="gap-1.5 border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
            <Volume2 className="h-3.5 w-3.5" /> Test / Audition
          </Button>
          {p.status !== "approved" ? (
            <Button size="sm" variant="ghost" data-testid={`pron-approve-${p.id}`} onClick={() => act("approve")} className="gap-1.5 text-emerald-300 hover:bg-emerald-500/10">
              <CheckCircle2 className="h-3.5 w-3.5" /> Approve
            </Button>
          ) : (
            <Button size="sm" variant="ghost" data-testid={`pron-return-draft-${p.id}`} onClick={() => act("return-to-draft")} className="gap-1.5 text-amber-300 hover:bg-amber-500/10">
              <Undo2 className="h-3.5 w-3.5" /> To draft
            </Button>
          )}
          {p.status !== "rejected" && (
            <Button size="sm" variant="ghost" data-testid={`pron-reject-${p.id}`} onClick={() => act("reject")} className="gap-1.5 text-red-300 hover:bg-red-500/10">
              <XCircle className="h-3.5 w-3.5" /> Reject
            </Button>
          )}
          <Button size="sm" variant="ghost" data-testid={`pron-edit-${p.id}`} onClick={() => onEdit(p)} className="gap-1.5 text-slate-400 hover:bg-slate-500/10">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Dictionary Sync panel ---------------- */
function DictionaryPanel({ projectId }) {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([
      api.get(`/projects/${projectId}/pronunciation-dictionary`).then((r) => r.data).catch(() => null),
      api.post(`/projects/${projectId}/pronunciation-dictionary/preview`).then((r) => r.data).catch(() => null),
    ]);
    setStatus(s); setPreview(p);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    if (!window.confirm("Create/update the remote ElevenLabs pronunciation dictionary with all APPROVED rules?")) return;
    setSyncing(true);
    try {
      await api.post(`/projects/${projectId}/pronunciation-dictionary/sync`, { confirm: true });
      toast.success("Pronunciation dictionary synced to ElevenLabs.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      await load();
    } finally { setSyncing(false); }
  };

  if (!status || !preview) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>;

  return (
    <div className="space-y-5" data-testid="dictionary-panel">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-[#12161f] p-4">
          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Remote dictionary</div>
          {status.configured ? (
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex items-center gap-2 text-emerald-300"><RadioTower className="h-4 w-4" /> Synced</div>
              <div className="font-mono text-xs text-slate-400">id: {status.remote_dictionary_id}</div>
              <div className="font-mono text-xs text-slate-400">version: {status.remote_version_id}</div>
              <div className="text-xs text-slate-500">{status.rule_count} rules · last synced {status.synced_at ? new Date(status.synced_at).toLocaleString() : "—"}</div>
            </div>
          ) : (
            <p className="mt-2 text-sm text-slate-400">Not synced yet. Approve rules and preview-sync below.</p>
          )}
          {status.last_error && <p className="mt-2 rounded bg-red-500/10 px-2 py-1 text-xs text-red-300">{status.last_error}</p>}
        </div>
        <div className="rounded-xl border border-border bg-[#12161f] p-4">
          <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Approved rules to sync</div>
          <div className="mt-2 font-mono text-3xl text-amber-300" data-testid="dict-approved-count">{preview.rule_count}</div>
          <p className="text-xs text-slate-500">{preview.would_create ? "Will CREATE a new remote dictionary." : "Will ADD a new version to the existing dictionary."}</p>
          <Button data-testid="dict-sync-btn" onClick={sync} disabled={syncing || preview.rule_count === 0}
            className="mt-3 w-full gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RadioTower className="h-4 w-4" />} Sync approved rules (confirm)
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-[#12161f] p-4">
        <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Preview · rules ({preview.rules.length})</div>
        {preview.rules.length === 0 ? (
          <p className="text-sm text-slate-400">No approved pronunciations yet. Approve entries in the queue to build the dictionary.</p>
        ) : (
          <div className="space-y-1" data-testid="dict-rules-preview">
            {preview.rules.map((r, i) => (
              <div key={i} className="flex items-center justify-between rounded bg-[#0d1016] px-3 py-1.5 text-sm">
                <span className="text-slate-200">{r.string_to_replace}</span>
                <span className="font-mono text-xs text-amber-300">{r.type === "phoneme" ? `/${r.phoneme}/ (${r.alphabet})` : r.alias}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {status.sync_history?.length > 0 && (
        <div className="rounded-xl border border-border bg-[#12161f] p-4">
          <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Sync history</div>
          <div className="space-y-1">
            {status.sync_history.slice().reverse().map((h, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className={h.ok ? "text-emerald-300" : "text-red-300"}>{h.action} {h.ok ? "✓" : "✕"}</span>
                <span className="text-slate-500">{h.rule_count} rules · {new Date(h.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Main ---------------- */
export function PronunciationManager() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [queue, setQueue] = useState([]);
  const [future, setFuture] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [audition, setAudition] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const poll = useRef(null);

  const load = useCallback(async () => {
    const [q, f] = await Promise.all([
      api.get(`/projects/${projectId}/pronunciations`).then((r) => r.data).catch(() => []),
      api.get(`/projects/${projectId}/pronunciations/future`).then((r) => r.data).catch(() => []),
    ]);
    setQueue(q); setFuture(f);
  }, [projectId]);

  useEffect(() => {
    api.get(`/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => {});
    load().finally(() => setLoading(false));
    return () => poll.current && clearInterval(poll.current);
  }, [projectId, load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/pronunciations/scan`);
      toast.info("Scanning the manuscript with Claude Sonnet 4.6…");
      pollScan(data.scan_id, "candidates");
    } catch (e) {
      setScanning(false);
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const runHomographScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/pronunciations/scan-homographs`);
      if (data.status === "completed") {
        setScanning(false);
        toast.success("No homograph occurrences found in this manuscript.");
        return;
      }
      toast.info(`Scanning ${data.occurrence_count} homograph occurrence(s) with Claude Sonnet 4.6…`);
      pollScan(data.scan_id, "homograph sense(s)");
    } catch (e) {
      setScanning(false);
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const pollScan = (scanId, noun) => {
    poll.current = setInterval(async () => {
      try {
        const { data: s } = await api.get(`/pronunciation-scans/${scanId}`);
        if (s.status === "completed") {
          clearInterval(poll.current); setScanning(false);
          toast.success(`Scan complete — ${s.added_count} ${noun} added as drafts.`);
          await load();
        } else if (s.status === "failed") {
          clearInterval(poll.current); setScanning(false);
          toast.error(`Scan failed: ${s.error || "unknown error"}`);
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const saveEdit = async (payload) => {
    try {
      await api.patch(`/pronunciations/${editing.id}`, payload);
      toast.success("Saved."); setEditing(null); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const saveAdd = async (payload) => {
    try {
      await api.post(`/projects/${projectId}/pronunciations`, payload);
      toast.success("Added as draft."); setAdding(false); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const deletePron = async () => {
    try {
      await api.delete(`/pronunciations/${editing.id}`);
      toast.success("Deleted."); setEditing(null); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const counts = {
    total: queue.length,
    draft: queue.filter((p) => p.status === "draft").length,
    approved: queue.filter((p) => p.status === "approved").length,
    rejected: queue.filter((p) => p.status === "rejected").length,
  };
  const homographs = queue.filter((p) => p.entry_type === "homograph");
  const terms = queue.filter((p) => p.entry_type !== "homograph");
  const firstSet = terms.filter((p) => p.first_audition_set);
  const rest = terms.filter((p) => !p.first_audition_set);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  return (
    <div>
      <button onClick={() => navigate(`/studio/projects/${projectId}`)} className="mb-4 flex items-center gap-1.5 text-xs text-slate-500 hover:text-amber-300" data-testid="pron-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to production
      </button>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-1 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/70">Phase 4 · Pronunciation</div>
          <h1 className="font-serif text-4xl tracking-tight text-slate-100">Pronunciation Manager</h1>
          <p className="mt-1 text-sm text-slate-400">{project?.title} · everything stays draft until you approve it.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="pron-add-btn" variant="outline" onClick={() => setAdding(true)} className="gap-2"><Plus className="h-4 w-4" /> Add term</Button>
          <Button data-testid="pron-scan-homographs-btn" variant="outline" onClick={runHomographScan} disabled={scanning} className="gap-2 border-teal-500/40 text-teal-200 hover:bg-teal-500/10">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shuffle className="h-4 w-4" />} Scan homographs
          </Button>
          <Button data-testid="pron-scan-btn" onClick={runScan} disabled={scanning} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Scan manuscript
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Candidates" value={counts.total} testid="stat-total" />
        <Stat label="Draft" value={counts.draft} tone="amber" testid="stat-draft" />
        <Stat label="Approved" value={counts.approved} tone="emerald" testid="stat-approved" />
        <Stat label="Rejected" value={counts.rejected} tone="red" testid="stat-rejected" />
      </div>

      <Tabs defaultValue="queue" className="mt-8">
        <TabsList className="bg-[#12161f]">
          <TabsTrigger value="queue" data-testid="tab-queue">Active queue ({terms.length})</TabsTrigger>
          <TabsTrigger value="homographs" data-testid="tab-homographs">Homographs ({homographs.length})</TabsTrigger>
          <TabsTrigger value="testbench" data-testid="tab-testbench">Test bench</TabsTrigger>
          <TabsTrigger value="future" data-testid="tab-future">Future & series ({future.length})</TabsTrigger>
          <TabsTrigger value="dictionary" data-testid="tab-dictionary">Dictionary sync</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-5 space-y-6">
          {firstSet.length > 0 && (
            <div>
              <div className="mb-3 flex items-center gap-2">
                <Star className="h-4 w-4 text-amber-400" />
                <h2 className="font-serif text-lg text-amber-200">First Scott audition set</h2>
                <span className="text-xs text-slate-500">— Onyx, Marisol & Andrea first</span>
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                {firstSet.map((p) => <PronCard key={p.id} p={p} projectId={projectId} onAudition={setAudition} onEdit={setEditing} onChange={load} />)}
              </div>
            </div>
          )}
          <div>
            {firstSet.length > 0 && <h2 className="mb-3 font-serif text-lg text-slate-300">Full review queue</h2>}
            <div className="grid gap-3 lg:grid-cols-2">
              {rest.map((p) => <PronCard key={p.id} p={p} projectId={projectId} onAudition={setAudition} onEdit={setEditing} onChange={load} />)}
            </div>
          </div>
          {homographs.length > 0 && (
            <div>
              <div className="mb-3 flex items-center gap-2">
                <Shuffle className="h-4 w-4 text-teal-400" />
                <h2 className="font-serif text-lg text-teal-200">Homographs in this chapter</h2>
                <span className="text-xs text-slate-500">— context decides pronunciation; generation-text only</span>
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                {homographs.map((p) => <PronCard key={p.id} p={p} projectId={projectId} onAudition={setAudition} onEdit={setEditing} onChange={load} />)}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="homographs" className="mt-5">
          <p className="mb-3 text-sm text-slate-400">
            <Shuffle className="mr-1 inline h-4 w-4 text-teal-400" />
            Context-sensitive words (bass, live, read, lead…). The manuscript text is never changed — the intended
            pronunciation is applied only to the generation text sent to ElevenLabs, at the listed occurrences. These
            never become global dictionary rules.
          </p>
          {homographs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-[#12161f]/40 py-16 text-center text-slate-400">
              No homographs yet. Run <span className="text-teal-300">Scan homographs</span> or add one manually.
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {homographs.map((p) => <PronCard key={p.id} p={p} projectId={projectId} onAudition={setAudition} onEdit={setEditing} onChange={load} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="testbench" className="mt-5">
          <PronunciationTestBench projectId={projectId} candidates={queue} onChange={load} />
        </TabsContent>

        <TabsContent value="future" className="mt-5">
          <p className="mb-3 text-sm text-slate-400">
            <BookText className="mr-1 inline h-4 w-4 text-slate-500" />
            Terms from the Product Specification that are not in the current Chapter One manuscript. Held for future chapters and the series library.
          </p>
          <div className="grid gap-3 lg:grid-cols-2">
            {future.map((p) => <PronCard key={p.id} p={p} projectId={projectId} onAudition={setAudition} onEdit={setEditing} onChange={load} />)}
          </div>
        </TabsContent>

        <TabsContent value="dictionary" className="mt-5">
          <DictionaryPanel projectId={projectId} />
        </TabsContent>
      </Tabs>

      {audition && <AuditionDialog pron={audition} onClose={() => setAudition(null)} onGenerated={load} />}
      {editing && <PronForm title={`Edit · ${editing.written_form}`} initial={editing} onSave={saveEdit} onClose={() => setEditing(null)} allowDelete={editing.source === "manual"} onDelete={deletePron} />}
      {adding && <PronForm title="Add pronunciation" onSave={saveAdd} onClose={() => setAdding(false)} />}
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const c = { amber: "text-amber-300", emerald: "text-emerald-300", red: "text-red-300" }[tone] || "text-slate-200";
  return (
    <div className="rounded-lg border border-border bg-[#12161f] px-4 py-3" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-2xl ${c}`}>{value}</div>
    </div>
  );
}
